import React, { useState, useEffect, useMemo, useRef } from "react";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AccountingNav from "./components/AccountingNav";
import GetPaymentHistory from "../../APIs/GetPaymentHistory";
import GetUnsettledShipmentsReport from "../../APIs/GetUnsettledShipmentsReport";
import config from "../../config.json";
import history from "../../APIs/History";

const hasAccountingAccess = () => {
    const clientCode = localStorage.getItem("carrierClientId");
    const carrierId = localStorage.getItem("carrierId");
    const entry = config.dropDownNavigation.find(e => e.clientCode === clientCode);
    if (!entry || !entry.routes.includes("Accounting")) return false;
    return entry.carrierIds === "*" || (Array.isArray(entry.carrierIds) && entry.carrierIds.includes(carrierId));
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

const formatCurrency = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    return Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const paymentStatus = (paid, total) => {
    if (!paid || Number(paid) === 0) {
        return <span className="badge badge-warning">Pending</span>;
    }
    if (Number(paid) === Number(total)) {
        return <span className="badge badge-success">Paid</span>;
    }
    return <span className="badge badge-info" title={`Paid: ${formatCurrency(paid)}`}>Partial</span>;
};

const statusLabel = (paid, total) => {
    if (!paid || Number(paid) === 0) return "Pending";
    if (Number(paid) === Number(total)) return "Paid";
    return "Partial";
};

const SortIcon = ({ col, sortCol, sortDir }) => {
    if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>⇅</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
};

const sortData = (data, col, dir) => {
    if (!col) return data;
    return [...data].sort((a, b) => {
        let aVal = a[col];
        let bVal = b[col];
        if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
            aVal = Number(aVal);
            bVal = Number(bVal);
        }
        if (aVal < bVal) return dir === "asc" ? -1 : 1;
        if (aVal > bVal) return dir === "asc" ? 1 : -1;
        return 0;
    });
};

const useSortable = (defaultCol, defaultDir = "desc") => {
    const [sortCol, setSortCol] = useState(defaultCol);
    const [sortDir, setSortDir] = useState(defaultDir);
    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortCol(col);
            setSortDir("asc");
        }
    };
    return { sortCol, sortDir, handleSort };
};

const thStyle = { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" };
const stickyThStyle = { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", position: "sticky", top: 0, backgroundColor: "#e9ecef", zIndex: 2 };
const stickyFirstThStyle = { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", position: "sticky", top: 0, left: 0, backgroundColor: "#e9ecef", zIndex: 3, boxShadow: "2px 0 4px rgba(0,0,0,0.12)" };
const stickyNoSortThStyle = { whiteSpace: "nowrap", position: "sticky", top: 0, backgroundColor: "#e9ecef", zIndex: 2 };
const stickyTdStyle = { position: "sticky", left: 0, backgroundColor: "#fff", zIndex: 1, boxShadow: "2px 0 4px rgba(0,0,0,0.08)" };

const CopyCell = React.memo(({ value, tdStyle }) => {
    const [state, setState] = React.useState("idle"); // idle | copied | fading
    const t1 = React.useRef(null);
    const t2 = React.useRef(null);

    const handleClick = () => {
        if (!value) return;
        navigator.clipboard.writeText(String(value));
        clearTimeout(t1.current);
        clearTimeout(t2.current);
        setState("copied");
        t1.current = setTimeout(() => setState("fading"), 1200);
        t2.current = setTimeout(() => setState("idle"), 1600);
    };

    return (
        <td style={{ cursor: value ? "pointer" : "default", ...tdStyle }} onClick={handleClick}>
            <span style={{
                display: "inline-block",
                transition: "opacity 0.4s",
                opacity: state === "fading" ? 0 : 1,
                color: state !== "idle" ? "#28a745" : "inherit",
            }}>
                {state !== "idle" ? "✓ Copied" : (value || "")}
            </span>
        </td>
    );
});

const ClipboardSVG = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
        <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1z"/>
        <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0z"/>
    </svg>
);

const CopyIcon = React.memo(({ value }) => {
    const [state, setState] = React.useState("idle");
    const t1 = React.useRef(null);
    const t2 = React.useRef(null);

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!value) return;
        navigator.clipboard.writeText(String(value));
        clearTimeout(t1.current);
        clearTimeout(t2.current);
        setState("copied");
        t1.current = setTimeout(() => setState("fading"), 1200);
        t2.current = setTimeout(() => setState("idle"), 1600);
    };

    return (
        <span
            onClick={handleClick}
            title="Copy to clipboard"
            style={{
                cursor: "pointer",
                marginLeft: 6,
                transition: "opacity 0.4s",
                opacity: state === "fading" ? 0 : 1,
                color: state !== "idle" ? "#28a745" : "#adb5bd",
                verticalAlign: "middle",
            }}
        >
            {state !== "idle" ? "✓" : <ClipboardSVG />}
        </span>
    );
});

const fileTimestamp = () => {
    const now = new Date();
    return now.toISOString().replace(/T/, "-").replace(/:/g, "-").slice(0, 16);
};

const downloadedAtLabel = () => {
    const now = new Date();
    return `Downloaded: ${now.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })} ${now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
};

const exportPDF = (title, headers, rows, carrierId) => {
    const doc = new jsPDF({ orientation: "landscape" });
    const downloaded = downloadedAtLabel();
    doc.setFontSize(13);
    doc.text(title, 14, 16);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(downloaded, 14, 23);
    doc.setTextColor(0);
    autoTable(doc, {
        startY: 28,
        head: [headers],
        body: rows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [52, 73, 94] },
    });
    doc.save(`${carrierId}-${fileTimestamp()}.pdf`);
};

const TableHeader = ({ title, csvData, csvHeaders, carrierId, pdfTitle, pdfHeaders, pdfRows }) => {
    const downloaded = downloadedAtLabel();
    const csvFilename = `${carrierId}-${fileTimestamp()}.csv`;

    // Build CSV as raw arrays so info rows appear above the column headers
    const csvRaw = [
        [downloaded],
        [],
        csvHeaders.map((h) => h.label),
        ...csvData.map((row) => csvHeaders.map((h) => row[h.key] ?? "")),
    ];

    return (
        <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="mb-0">{title}</h4>
            <div>
                <CSVLink
                    data={csvRaw}
                    filename={csvFilename}
                    className="btn btn-sm btn-outline-secondary mr-1"
                    title="Export CSV"
                >
                    <i className="fas fa-file-csv" />
                </CSVLink>
                <button
                    className="btn btn-sm btn-outline-secondary"
                    title="Export PDF"
                    onClick={() => exportPDF(pdfTitle, pdfHeaders, pdfRows, carrierId)}
                >
                    <i className="fas fa-file-pdf" />
                </button>
            </div>
        </div>
    );
};

const Accounting = () => {
    const carrierId = localStorage.getItem("carrierId");
    const [paymentSummary, setPaymentSummary] = useState([]);
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const summary = useSortable("setlrepdate", "desc");
    const detail = useSortable("setlrepdate", "desc");
    const [shipmentSearchInput, setShipmentSearchInput] = useState("");
    const [shipmentSearch, setShipmentSearch] = useState("");
    const filterRef = useRef(null);

    const drillDown = (row) => {
        const term = checkBySettlement[row.id] || row.setlrepid || "";
        setShipmentSearchInput(term);
        setShipmentSearch(term);
        setTimeout(() => filterRef.current && filterRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    };

    const openUnsettledShipmentsReport = async () => {
        const newTab = window.open("", "_blank");
        try {
            const res = await GetUnsettledShipmentsReport.getByCarrierId(carrierId);
            const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            if (newTab) newTab.location.href = blobUrl;
        } catch (err) {
            console.error("GetUnsettledShipmentsReport error", err);
            if (newTab) newTab.close();
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => setShipmentSearch(shipmentSearchInput), 200);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipmentSearchInput]);


    const accessAllowed = hasAccountingAccess();

    useEffect(() => {
        if (!carrierId || !accessAllowed) { setLoading(false); return; }
        GetPaymentHistory.getByCarrierId(carrierId)
            .then((res) => {
                setPaymentSummary(res.data.paymentSummary || []);
                setShipments(res.data.shipments || []);
            })
            .catch((err) => {
                console.error("GetPaymentHistory error", err);
                setError("Unable to load payment history.");
            })
            .finally(() => setLoading(false));
            // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carrierId]);

    const sortedSummary = useMemo(
        () => sortData(paymentSummary, summary.sortCol, summary.sortDir),
        [paymentSummary, summary.sortCol, summary.sortDir]
    );
    const sortedShipments = useMemo(
        () => sortData(shipments, detail.sortCol, detail.sortDir),
        [shipments, detail.sortCol, detail.sortDir]
    );
    const filteredShipments = useMemo(() => {
        if (!shipmentSearch.trim()) return sortedShipments;
        const term = shipmentSearch.toLowerCase();
        return sortedShipments.filter(r =>
            [r.webquotecontrol, r.pronum, r.invoiceNum, r.checkNumber, r.setlrepid,
             String(r.provider_approved || ""), String(r.carrierSettlementTotal || "")]
            .some(v => v && String(v).toLowerCase().includes(term))
        );
    }, [sortedShipments, shipmentSearch]);

    const checkBySettlement = useMemo(() => {
        const map = {};
        shipments.forEach(s => {
            if (s.checkNumber && !map[s.setlreport_id]) map[s.setlreport_id] = s.checkNumber;
        });
        return map;
    }, [shipments]);

    // CSV configs — reflect current sort order
    const summaryCsvHeaders = [
        { label: "Settlement ID", key: "setlrepid" },
        { label: "Settlement Date", key: "setlrepdate" },
        { label: "Shipments", key: "shipmentCount" },
        { label: "Check / ACH #", key: "checkNumber" },
        { label: "Payment Date", key: "paymentDate" },
        { label: "Amount", key: "carrierTotal" },
        { label: "Status", key: "status" },
    ];
    const summaryCsvData = useMemo(() => sortedSummary.map((r) => ({
        ...r,
        setlrepdate: formatDate(r.setlrepdate),
        checkNumber: checkBySettlement[r.id] || "",
        paymentDate: r.paymentDate ? formatDate(r.paymentDate) : "",
        status: statusLabel(r.totalPayments, r.carrierTotal),
    })), [sortedSummary, checkBySettlement]);

    const shipmentsCsvHeaders = [
        { label: "Shipment", key: "webquotecontrol" },
        { label: "PRO #", key: "pronum" },
        { label: "Invoice #", key: "invoiceNum" },
        { label: "Pickup Date", key: "pudate" },
        { label: "Delivery Date", key: "deldate" },
        { label: "Shipment Amount", key: "provider_approved" },
        { label: "Settlement Date", key: "setlrepdate" },
        { label: "Settlement ID", key: "setlrepid" },
        { label: "Status", key: "status" },
        { label: "Check / ACH #", key: "checkNumber" },
        { label: "Payment Date", key: "paymentDate" },
        { label: "Settlement Deposit", key: "carrierSettlementTotal" },
    ];
    const shipmentsCsvData = useMemo(() => sortedShipments.map((r) => ({
        ...r,
        pudate: formatDate(r.pudate),
        deldate: formatDate(r.deldate),
        setlrepdate: formatDate(r.setlrepdate),
        paymentDate: r.paymentDate ? formatDate(r.paymentDate) : "",
        status: statusLabel(r.checkAmount, r.provider_approved),
    })), [sortedShipments]);

    // PDF rows — reflect current sort order
    const summaryPdfHeaders = ["Settlement ID", "Settlement Date", "Shipments", "Check / ACH #", "Payment Date", "Amount", "Status"];
    const summaryPdfRows = useMemo(() => sortedSummary.map((r) => [
        r.setlrepid,
        formatDate(r.setlrepdate),
        r.shipmentCount,
        checkBySettlement[r.id] || "",
        r.paymentDate ? formatDate(r.paymentDate) : "",
        formatCurrency(r.carrierTotal),
        statusLabel(r.totalPayments, r.carrierTotal),
    ]), [sortedSummary, checkBySettlement]);

    const shipmentsPdfHeaders = ["Shipment", "PRO #", "Invoice #", "Pickup", "Delivery", "Amount", "Settlement Date", "Settlement ID", "Status", "Check / ACH #", "Payment Date", "Deposit"];
    const shipmentsPdfRows = useMemo(() => sortedShipments.map((r) => [
        r.webquotecontrol,
        r.pronum || "",
        r.invoiceNum || "",
        formatDate(r.pudate),
        formatDate(r.deldate),
        formatCurrency(r.provider_approved),
        formatDate(r.setlrepdate),
        r.setlrepid,
        statusLabel(r.checkAmount, r.provider_approved),
        r.checkNumber || "",
        r.paymentDate ? formatDate(r.paymentDate) : "",
        formatCurrency(r.carrierSettlementTotal),
    ]), [sortedShipments]);

    return (
        <div>
            <AccountingNav />
            <div style={{ width: '100vw', position: 'relative', left: '50%', marginLeft: '-50vw', paddingLeft: '8px', paddingRight: '8px', boxSizing: 'border-box' }}>
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <h4 className="mb-0">Payment History</h4>
                    <div className="d-flex" style={{ gap: '8px' }}>
                        <div className="border rounded p-2" style={{ fontSize: '12px', lineHeight: '1.8' }}>
                            <div><span className="badge badge-success mr-1">Paid</span> Payment received and matched to approved amount.</div>
                            <div><span className="badge badge-warning mr-1">Pending</span> No payment recorded yet.</div>
                            <div><span className="badge badge-info mr-1">Partial</span> Payment received but does not match approved amount.</div>
                        </div>
                        <div className="border rounded p-2" style={{ fontSize: '12px', lineHeight: '1.6', maxWidth: '340px', color: '#666' }}>
                            * If ACH, this was the date the payment was scheduled with our ACH payment provider Bill.com. Allow 2–5 business days for payment to be received by your bank. You can track ACH status by logging into your Bill.com account.
                        </div>
                    </div>
                </div>

                {accessAllowed && (
                    <div className="d-flex justify-content-end mb-3">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            title="Unsettled Shipments Report"
                            onClick={openUnsettledShipmentsReport}
                        >
                            <i className="fas fa-file-csv mr-1" /> Unsettled Shipments
                        </button>
                    </div>
                )}

                {!accessAllowed && (
                    <div className="alert alert-secondary">Payment history is not configured for this carrier.</div>
                )}

                {accessAllowed && loading && (
                    <div className="d-flex align-items-center text-muted mb-3">
                        <div className="spinner-border spinner-border-sm mr-2" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        Loading payment history...
                    </div>
                )}

                {accessAllowed && error && (
                    <div className="alert alert-danger">{error}</div>
                )}

                {accessAllowed && !loading && !error && (
                    <>
                        {/* SUMMARY TABLE */}
                        <TableHeader
                            title="Settlements (last 12 months)"
                            csvData={summaryCsvData}
                            csvHeaders={summaryCsvHeaders}
                            carrierId={carrierId}
                            pdfTitle="Settlements"
                            pdfHeaders={summaryPdfHeaders}
                            pdfRows={summaryPdfRows}
                        />
                        <div className="table-responsive mb-5">
                            <table className="table table-sm table-bordered table-striped">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={thStyle} onClick={() => summary.handleSort("setlrepid")}>
                                            Settlement ID <SortIcon col="setlrepid" sortCol={summary.sortCol} sortDir={summary.sortDir} />
                                        </th>
                                        <th style={thStyle} onClick={() => summary.handleSort("setlrepdate")}>
                                            Settlement Date <SortIcon col="setlrepdate" sortCol={summary.sortCol} sortDir={summary.sortDir} />
                                        </th>
                                        <th style={thStyle} onClick={() => summary.handleSort("shipmentCount")}>
                                            Shipments <SortIcon col="shipmentCount" sortCol={summary.sortCol} sortDir={summary.sortDir} />
                                        </th>
                                        <th>Check / ACH #</th>
                                        <th style={thStyle} onClick={() => summary.handleSort("paymentDate")}>
                                            Payment Date * <SortIcon col="paymentDate" sortCol={summary.sortCol} sortDir={summary.sortDir} />
                                        </th>
                                        <th style={thStyle} onClick={() => summary.handleSort("carrierTotal")}>
                                            Amount <SortIcon col="carrierTotal" sortCol={summary.sortCol} sortDir={summary.sortDir} />
                                        </th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedSummary.length === 0 ? (
                                        <tr><td colSpan="7" className="text-muted text-center">No settlements found.</td></tr>
                                    ) : sortedSummary.map((row) => (
                                        <tr key={row.id}>
                                            <CopyCell value={row.setlrepid} />
                                            <td>{formatDate(row.setlrepdate)}</td>
                                            <td
                                                style={{ cursor: "pointer", textDecoration: "underline dotted", color: "#0066cc" }}
                                                onClick={() => drillDown(row)}
                                                title="Click to filter shipments"
                                            >{row.shipmentCount}</td>
                                            <CopyCell value={checkBySettlement[row.id] || ""} />
                                            <td>{row.paymentDate ? formatDate(row.paymentDate) : ""}</td>
                                            <td>{formatCurrency(row.carrierTotal)}</td>
                                            <td>{paymentStatus(row.totalPayments, row.carrierTotal)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* SHIPMENT DETAIL TABLE */}
                        <TableHeader
                            title="Shipment Detail"
                            csvData={shipmentsCsvData}
                            csvHeaders={shipmentsCsvHeaders}
                            carrierId={carrierId}
                            pdfTitle="Shipment Detail"
                            pdfHeaders={shipmentsPdfHeaders}
                            pdfRows={shipmentsPdfRows}
                        />
                        <div ref={filterRef} className="input-group input-group-sm mb-2" style={{ maxWidth: "320px" }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Filter shipments..."
                                value={shipmentSearchInput}
                                onChange={e => setShipmentSearchInput(e.target.value)}
                            />
                            {shipmentSearchInput && (
                                <div className="input-group-append">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => { setShipmentSearchInput(""); setShipmentSearch(""); }}
                                        title="Clear filter"
                                    >
                                        <i className="fas fa-times" />
                                    </button>
                                </div>
                            )}
                        </div>
                        <p className="text-muted small mb-2">
                            The <strong>Settlement Deposit</strong> column is your total for that settlement batch — use it to match against your bank statement.
                        </p>
                        <div className="table-responsive mb-5" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                            <table className="table table-sm table-bordered table-striped">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={stickyFirstThStyle} onClick={() => detail.handleSort("webquotecontrol")}>
                                            Shipment <SortIcon col="webquotecontrol" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("pronum")}>
                                            PRO # <SortIcon col="pronum" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("invoiceNum")}>
                                            Invoice # <SortIcon col="invoiceNum" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("pudate")}>
                                            Pickup Date <SortIcon col="pudate" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("deldate")}>
                                            Delivery Date <SortIcon col="deldate" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("provider_approved")}>
                                            Shipment Amount <SortIcon col="provider_approved" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("setlrepdate")}>
                                            Settlement Date <SortIcon col="setlrepdate" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("setlrepid")}>
                                            Settlement ID <SortIcon col="setlrepid" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyNoSortThStyle}>Status</th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("checkNumber")}>
                                            Check / ACH # <SortIcon col="checkNumber" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("paymentDate")}>
                                            Payment Date * <SortIcon col="paymentDate" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                        <th style={stickyThStyle} onClick={() => detail.handleSort("carrierSettlementTotal")}>
                                            Settlement Deposit <SortIcon col="carrierSettlementTotal" sortCol={detail.sortCol} sortDir={detail.sortDir} />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredShipments.length === 0 ? (
                                        <tr><td colSpan="12" className="text-center">{shipmentSearch ? <span>No shipments match your filter. Please go to the <a href="/Dashboard" onClick={e => { e.preventDefault(); history.push("/Dashboard"); }}>Dashboard</a> and search for shipments that have not yet entered Accounting.</span> : "No shipments found."}</td></tr>
                                    ) : filteredShipments.map((row, i) => (
                                        <tr key={`${row.webquotecontrol}-${i}`}>
                                            <td style={{ ...stickyTdStyle, backgroundColor: i % 2 === 0 ? "#f2f2f2" : "#fff" }}>
                                                <a href={`/Dashboard?search=${encodeURIComponent(row.webquotecontrol)}`} target="_blank" rel="noopener noreferrer">
                                                    {row.webquotecontrol}
                                                </a>
                                                <CopyIcon value={row.webquotecontrol} />
                                            </td>
                                            <CopyCell value={row.pronum || ""} />
                                            <CopyCell value={row.invoiceNum || ""} />
                                            <td>{formatDate(row.pudate)}</td>
                                            <td>{formatDate(row.deldate)}</td>
                                            <td>{formatCurrency(row.provider_approved)}</td>
                                            <td>{formatDate(row.setlrepdate)}</td>
                                            <CopyCell value={row.setlrepid} />
                                            <td>{paymentStatus(row.checkAmount, row.provider_approved)}</td>
                                            <CopyCell value={row.checkNumber || ""} />
                                            <td>{row.paymentDate ? formatDate(row.paymentDate) : ""}</td>
                                            <td>{formatCurrency(row.carrierSettlementTotal)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
            <div style={{ textAlign: "center", marginBottom: "10px" }}>
                <div style={{ fontSize: "12px", color: "#aaa", marginTop: "6px" }}>Powered by</div>
                <img src="shippersedge-logo.png" alt="ShippersEdge" height="70" />

            </div>
        </div>
    );
};

export default Accounting;
